import type { HouseListItem } from '../../api/station/stationApiTypes';

interface AddressParts {
    street?: string | null;
    houseNumber?: string | null;
    postalCode?: string | null;
    city?: string | null;
}

const norm = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();

/**
 * The houses standing at exactly this room's address — the only ones "Dem Haus
 * zuordnen" offers (plan room-houses-and-combining-rooms §4 row 3). Identical
 * means street, number, postcode and city, trimmed and case-insensitive; never
 * fuzzy. The server checks the same and refuses otherwise.
 *
 * A house on the room's OWN location is offered too: the server then ADOPTS the
 * room (it changes only its house, nothing moves — `mode: 'adopt'`), see
 * `isAdoption` for the confirmation it gets.
 */
export const housesAtAddress = (
    houses: HouseListItem[],
    address: AddressParts | null | undefined,
): HouseListItem[] => {
    if (!address || !norm(address.street)) return [];
    return houses.filter((house) => house.address
        && norm(house.address.street) === norm(address.street)
        && norm(house.address.houseNumber) === norm(address.houseNumber)
        && norm(house.address.postalCode) === norm(address.postalCode)
        && norm(house.address.city) === norm(address.city));
};

/**
 * Does this house stand at the room's own location? Then nothing moves: the
 * room only changes its house (`consolidateRoom`, `mode: 'adopt'`), and the
 * confirmation must not say the location is archived. `ownLocationId` is the
 * location the server compares (`Group.location_id`); null when unknown.
 */
export const isAdoption = (house: HouseListItem | undefined, ownLocationId: string | null | undefined): boolean =>
    Boolean(house && ownLocationId && house.locationId === ownLocationId);

/**
 * The houses a room may be assigned to (ADR-0025 amendment 2026-10-12): the
 * other houses at its identical address, offered only while the room stands
 * alone in its own house — or in no house yet, on an automatic one-room
 * station from before. A room in a house with other rooms is never moved.
 */
export const assignableHouses = (
    houses: HouseListItem[],
    ownHouseId: string | null,
    address: AddressParts | null | undefined,
): HouseListItem[] => {
    const own = ownHouseId ? houses.find((house) => house.id === ownHouseId) : undefined;
    if (ownHouseId && (!own || own.roomCount !== 1)) return [];
    return housesAtAddress(houses, address).filter((house) => house.id !== ownHouseId);
};

const REFUSALS = new Set([
    'unsupported_tenant', 'room_not_found', 'room_not_alone', 'already_in_house', 'target_not_a_house',
    'same_location', 'address_differs', 'target_not_bookable',
    'commercial_model_at_source', 'target_has_buffer', 'family_would_split', 'location_sheet_conflict',
    'room_has_lock', 'not_a_room', 'location_mismatch', 'location_wide_code', 'conflict',
]);

/** The i18n key for a server refusal, or null for an unknown reason. */
export const consolidationRefusalKey = (reason: string | null | undefined): string | null => (
    reason && REFUSALS.has(reason) ? `asset_detail.assign_house.refusals.${reason}` : null
);
